import { useState } from 'react'

import { getDsModelAPI } from 'utils/getDsModelAPI'

import type { ErrorResponse } from 'types/types'

// Types based on Python dataclasses
export interface CenterSupplierInfo {
  optimizeType: string
  trMinOrderQuantityOnce: number
  trOrderLeadtime: number
}

export interface StockList {
  companyId: number
  centerId: number
  orgSupplierId: string
  itemCode: string
  shipQuantity: number
  shipQuantityOptimizeBase: number
  stockQuantity: number
  arrivalQuantity: number
  arrivalFixFlg: boolean
  recDate: Date | null
  orderDate: Date | null
  safetyStockSetting: number | null
  stockDaysSetting: number | null
  minStockQuantity: number | null
  caseLot: number
  minOrderQuantity: number | null
  orderUnitType: string
  weight: number
  arrivalRatio: number
  stopArrival: boolean
  avgShipQuantityStockBase: number
}

export interface OrderQuantityOptimizeRequest {
  operationDate: Date
  shipPreviousStockFlg: boolean
  centerSupplierInfo: CenterSupplierInfo
  stockList: StockList[]
  optimizeRange?: number // default 14
}

export interface OrderRecommend {
  companyId: number
  centerId: number
  orgSupplierId: string
  itemCode: string
  arrivalDate: string
  optimizeRange: number
  orderRecommendCalcDatetime: string
  orderQuantityRecommend: number
  orderQuantityLpResult?: number | null
  minStockForecastBased?: number | null
  minStockShortageBased?: number | null
  calcSafety?: number | null
}

export interface OrderQuantityOptimizeResponse {
  orderRecommendList: OrderRecommend[]
}

export interface OrderQuantityOptimizeResult {
  status: 'success' | 'error'
  message: string
  systemDate: string | null
  pageNo: number
  systemInfo: any | null
}

const validateRequest = (request: OrderQuantityOptimizeRequest): string | null => {
  if (!request.operationDate) {
    return '運用日は必須です。'
  }

  if (typeof request.shipPreviousStockFlg !== 'boolean') {
    return '前在庫出荷フラグは必須です。'
  }

  if (!request.centerSupplierInfo) {
    return 'センターサプライヤー情報は必須です。'
  }

  if (!request.centerSupplierInfo.optimizeType) {
    return '最適化タイプは必須です。'
  }

  if (typeof request.centerSupplierInfo.trMinOrderQuantityOnce !== 'number' || request.centerSupplierInfo.trMinOrderQuantityOnce <= 0) {
    return '最小発注数量は正の数で入力してください。'
  }

  if (typeof request.centerSupplierInfo.trOrderLeadtime !== 'number' || request.centerSupplierInfo.trOrderLeadtime < 0) {
    return '発注リードタイムは0以上の数で入力してください。'
  }

  if (!request.stockList || request.stockList.length === 0) {
    return '在庫リストは必須です。'
  }

  // Validate stock list items
  for (let i = 0; i < request.stockList.length; i += 1) {
    const stock = request.stockList[i]
    if (!stock.itemCode) {
      return `在庫リスト[${i}]: アイテムコードは必須です。`
    }
    if (typeof stock.companyId !== 'number' || stock.companyId <= 0) {
      return `在庫リスト[${i}]: 会社IDは正の数で入力してください。`
    }
    if (typeof stock.centerId !== 'number' || stock.centerId <= 0) {
      return `在庫リスト[${i}]: センターIDは正の数で入力してください。`
    }
    if (!stock.orgSupplierId) {
      return `在庫リスト[${i}]: サプライヤーIDは必須です。`
    }
  }

  if (request.optimizeRange !== undefined && (typeof request.optimizeRange !== 'number' || request.optimizeRange <= 0)) {
    return '最適化範囲は正の数で入力してください。'
  }

  return null
}

// Convert camelCase to snake_case for API request
const convertToSnakeCase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj
  }

  // Handle Date objects - convert to YYYY-MM-DD string format
  if (obj instanceof Date) {
    return obj.toISOString().split('T')[0]
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertToSnakeCase(item))
  }

  if (typeof obj === 'object') {
    const converted: any = {}
    Object.keys(obj).forEach((key) => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      converted[snakeKey] = convertToSnakeCase(obj[key])
    })
    return converted
  }

  return obj
}

// Convert snake_case to camelCase for API response
const convertToCamelCase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertToCamelCase(item))
  }

  if (typeof obj === 'object') {
    const converted: any = {}
    Object.keys(obj).forEach((key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
      converted[camelKey] = convertToCamelCase(obj[key])
    })
    return converted
  }

  return obj
}

export const useOrderQuantityOptimize = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<OrderQuantityOptimizeResult | undefined>(undefined)
  const [optimizeData, setOptimizeData] = useState<OrderQuantityOptimizeResponse | undefined>(undefined)

  const optimizeOrderQuantity = async (request: OrderQuantityOptimizeRequest): Promise<OrderQuantityOptimizeResponse | undefined> => {
    setResult(undefined)
    setIsLoading(true)

    // Validate request
    const validationError = validateRequest(request)
    if (validationError) {
      const errorResult: OrderQuantityOptimizeResult = {
        status: 'error',
        message: validationError,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      throw new Error(validationError)
    }

    try {
      
      const api = await getDsModelAPI()
      if (!api) {
        throw new Error('認証エラーが発生しました。')
      }

      // Convert request to snake_case for API
      const requestBody = convertToSnakeCase({
        ...request,
        optimizeRange: request.optimizeRange ?? 14,
      })

      const response = await api.optimizeOrderQuantity({
        orderQuantityOptimizeRequest: requestBody
      })
      
      // Convert response from snake_case to camelCase
      const convertedResponse = convertToCamelCase(response) as OrderQuantityOptimizeResponse

      setOptimizeData(convertedResponse)
      setResult({
        status: 'success',
        message: '発注数量最適化が完了しました。',
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      })
      return convertedResponse
    } catch (err) {
      const error = err as ErrorResponse
      const errorMessage = error.response?.status === 422 
        ? '入力値に誤りがあります。'
        : error.response?.statusText || '発注数量最適化でエラーが発生しました。'
      const errorResult: OrderQuantityOptimizeResult = {
        status: 'error',
        message: errorMessage,
        systemDate: null,
        pageNo: 0,
        systemInfo: null,
      }
      setResult(errorResult)
      return undefined
    } finally {
      setIsLoading(false)
    }
  }

  return {
    result,
    optimizeData,
    optimizeOrderQuantity,
    isLoading,
  }
}

